/**
 * apps/web/src/components/ThemeToggle.tsx
 *
 * Light / Dark / System appearance switch. Backed by HeroUI v3's built-in
 * `useTheme` hook, which persists the choice to localStorage (key
 * `heroui-theme`), resolves "system" against the OS preference, and applies the
 * result to <html> as BOTH a `.dark` / `.light` class and a `data-theme`
 * attribute - which is exactly what the glarno token layer keys off
 * (`:root` for light, `.dark, [data-theme="dark"]` for dark).
 *
 * There is no HeroUIProvider in v3; the hook is self-contained. This is the only
 * consumer today, so no shared theme context is needed yet.
 *
 * Styled on the semantic token layer (surface / border / accent), so it reads
 * correctly in both light and dark. Mounted in the Layout sidebar footer and the
 * mobile "Altro" drawer.
 */

import type React from 'react';
import { useTheme } from '@heroui/react';
import { Sun, Moon, Monitor } from 'lucide-react';
import { useStrings } from '../i18n';
import { cn } from '../lib/cn';

type ThemeChoice = 'light' | 'dark' | 'system';

export function ThemeToggle({ className }: { className?: string }): React.ReactElement {
  // Default `system`, matching the anti-FOUC script in index.html and the
  // marketing site: a visitor who never touched the toggle gets their OS
  // preference. Keep the two defaults in sync or the first paint flips.
  const { theme, setTheme } = useTheme('system');
  const strings = useStrings();

  const options: ReadonlyArray<{ value: ThemeChoice; label: string; Icon: typeof Sun }> = [
    { value: 'light', label: strings.impostazioni.themeLight, Icon: Sun },
    { value: 'dark', label: strings.impostazioni.themeDark, Icon: Moon },
    { value: 'system', label: strings.impostazioni.themeSystem, Icon: Monitor },
  ];

  return (
    <div
      // biome-ignore lint/a11y/useSemanticElements: <fieldset> is for grouped form
      // controls and its UA min-inline-size breaks this inline-flex segmented control.
      // role="group" + aria-label is the correct ARIA for a group of buttons.
      role="group"
      aria-label={strings.impostazioni.themeTitle}
      className={cn('inline-flex rounded-lg border border-border bg-surface p-0.5 shadow-sm', className)}
    >
      {options.map((opt) => {
        const active = opt.value === theme;
        return (
          <button
            key={opt.value}
            type="button"
            aria-pressed={active}
            aria-label={opt.label}
            title={opt.label}
            onClick={() => setTheme(opt.value)}
            className={cn(
              'rounded-md px-2 py-1 transition-colors',
              active ? 'bg-accent text-accent-foreground' : 'text-muted hover:text-foreground',
            )}
          >
            <opt.Icon size={15} aria-hidden />
          </button>
        );
      })}
    </div>
  );
}
